/* Copyright 2020 Enrico Ros. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

/*
Connects to the API endpoint via socket.io.
 */
import io from "socket.io-client";

// configuration
const FORCE_LOCALHOST_STAGE_BACKEND = false;
export const BACKEND_SERVER = FORCE_LOCALHOST_STAGE_BACKEND ?
  'http://localhost:1997' :       // when running the server locally
  'https://www.soulreplica.com'   // production server for socket.io (note that env.PUBLIC_URL will reflect re-bases)
const SOCKET_BACKEND_PATH_BASE = FORCE_LOCALHOST_STAGE_BACKEND ?
  '' :                            // locally
  (process.env.PUBLIC_URL || ''); // when in production, and the api is behind a proxy that relocates it
export const API_BASE = SOCKET_BACKEND_PATH_BASE + '/api';
const API_SOCKET_STAGE = API_BASE + '/socket/stage';


/// SUBSCRIBER types ///

// whole-config subscribers (ViewChatsList, DrawerItems)
export interface ConfSubscriber {
  onConf: (chats: ChatData[], stage: StageTypes.StageSnapshot) => void,
}

// channel-switch subscribers (TopBar)
export interface ChatSnapshotSubscriber {
  onChatSnapshot: (chatSnapshot: StageTypes.ChatSnapshot | null) => void,
}

// single-chat subscribers (ViewChat)
export interface ChatSubscriber {
  onChatSnapshot: (chatSnapshot: StageTypes.ChatSnapshot, uiLines: UILineItem[], speaker: string) => void,
  onChatUILines: (chatLines: UILineItem[]) => void,
  onChatTyping: (speaker: string, unixSeconds: number) => void,
}

// single-queue subscribers
export interface QueueSubscriber {
  onQueueUpdated: (queueId: string, contents: any[], updatedTs: number) => void,
}


/// Data Types ///

export enum UIDividerType {
  LineSkip = 1,
  NewStory,
  FutureStory,
}

export interface UIDivider {
  dividerType: UIDividerType,
  skipDelta?: number,
}

export interface UILineItem {
  uid: string,
  ts: number,
  // either of these, is set
  chatLine?: StageTypes.IChatLine,
  divider?: UIDivider,
}


// util
const err = (msg: any) => console.log('E: ' + msg);

function ensureContainer<T>(uid: string, objectCollection: { [uid: string]: T }, constructorFn: new (uid: string) => T): T {
  if (!(uid in objectCollection))
    objectCollection[uid] = new constructorFn(uid);
  return objectCollection[uid];
}

function getContainer<T>(key: string, objectCollection: { [key: string]: T }, notFoundMessage: string | undefined): T | null {
  if (key in objectCollection)
    return objectCollection[key];
  notFoundMessage && err(`${notFoundMessage} (${key})`);
  return null;
}

function subscribe<T>(subscriber: T, collection: T[], alreadySubMsg: string) {
  if (collection.indexOf(subscriber) !== -1) return err(`subscribe: ${alreadySubMsg}`);
  collection.push(subscriber);
}

function unsubscribe<T>(subscriber: T, collection: T[], notSubMsg: string): void {
  const index = collection.indexOf(subscriber);
  if (index === -1) return err(`unsubscribe: ${notSubMsg}`);
  collection.splice(index, 1);
}


/**
 * Client-side representation of a chat.
 * Note: this needs to be kept in sync with the counterpart in server/stage_api.
 */
class ChatData {
  // how many lines to keep of the chat history - past this point, the oldest are discarded
  PER_CHAT_MAX_CLIENT_HISTORY = 200;

  // conf/chan (stage) refreshed parameters
  readonly chatId: string;
  chatSnapshot: StageTypes.ChatSnapshot | null = null;
  // readonly public = true;

  // diff (stage) refreshed parameters
  uiLines: UILineItem[] = [];

  // type (stage) refreshed parameters
  typingSpeaker = "";

  // subscriptions to ChatData updates
  private subscribers: ChatSubscriber[] = [];


  constructor(chatId: string) {
    this.chatId = chatId;
  }

  // keep the following in sync with the server stage.js //

  updateSnapshot(s: StageTypes.ChatSnapshot) {
    // Note: we may need a deep copy here, but since the objects incoming are new, we won't need it
    this.chatSnapshot = s;
    // -> all:onChatSnapshot
    for (const subscriber of this.subscribers)
      subscriber.onChatSnapshot(this.chatSnapshot, this.uiLines, this.typingSpeaker);   // from onServerConf and onServerChan
  }

  updateTyping(speaker: string) {
    this.typingSpeaker = speaker;
    const speakerTimeStamp = ~~(Date.now() / 1000);
    // -> all:onChatTyping
    for (const subscriber of this.subscribers)
      subscriber.onChatTyping(speaker, speakerTimeStamp);
  }

  integrateDiff(linesDiff: StageTypes.IChatLine[]) {
    // chat lines come with largest seq on top. reverse it.
    linesDiff = linesDiff.reverse();
    for (const chatLine of linesDiff) {
      let prevUiLine = this.uiLines.length ? this.uiLines.slice(-1)[0] : null;

      // if the previous item was a FutureStory, remove it - we have a new chatline now
      if (prevUiLine && prevUiLine.divider && prevUiLine.divider.dividerType === UIDividerType.FutureStory) {
        this.uiLines.pop();
        prevUiLine = this.uiLines.length ? this.uiLines.slice(-1)[0] : null;
      }

      // Sequence - warn for out-of-order, show Skips
      if (prevUiLine && prevUiLine.chatLine) {
        const prevChatLineSeq = prevUiLine.chatLine ? prevUiLine.chatLine.seq : 0;
        const chatLineSeq = chatLine.seq;
        if (chatLineSeq <= prevChatLineSeq) {
          err(`chat line for ${this.chatId} is out of sequence (${chatLineSeq} <= ${prevChatLineSeq}). skipping.`);
          continue;
        }
        if (prevChatLineSeq && chatLineSeq > (prevChatLineSeq + 1)) {
          // +divider: Skip lines
          this.uiLines.push({
            uid: chatLine.uid + '-skip',
            ts: chatLine.ts,
            divider: {
              dividerType: UIDividerType.LineSkip,
              skipDelta: chatLineSeq - prevChatLineSeq,
            },
          })
        }
      }

      // +divider: New Story
      if (chatLine.segFirst) {
        this.uiLines.push({
          uid: chatLine.uid + '-seq',
          ts: chatLine.ts,
          divider: {
            dividerType: UIDividerType.NewStory,
          },
        });
      }

      // ChatLine
      this.uiLines.push({
        uid: chatLine.uid.toString(),
        ts: chatLine.ts,
        chatLine: chatLine,
      });
    }

    // remove items in excess of CHAT_MAX_HISTORY count
    const extraItems = this.uiLines.length - this.PER_CHAT_MAX_CLIENT_HISTORY;
    if (extraItems > 0)
      this.uiLines.splice(0, extraItems);

    // remove items before the first story - to start with a NewStory divider
    const firstStoryIdx = this.uiLines.findIndex((uiItem) => uiItem.divider && uiItem.divider.dividerType === UIDividerType.NewStory);
    if (firstStoryIdx > 0)
      this.uiLines.splice(0, firstStoryIdx);

    // -> all:onChatUILines
    for (const subscriber of this.subscribers)
      subscriber.onChatUILines(this.uiLines);
  }

  updateSegment(segId: number, segStartTs: number) {
    // +divider: Future Story (will be removed when the next line comes)
    this.uiLines.push({
      uid: segId + '-future',
      ts: segStartTs,
      divider: {
        dividerType: UIDividerType.FutureStory,
      },
    })

    // -> all:onChatUILines
    for (const subscriber of this.subscribers)
      subscriber.onChatUILines(this.uiLines);
  }

  // subscription methods //
  addSubscriber(subscriber: ChatSubscriber) {
    subscribe(subscriber, this.subscribers, 'already subscribed to Chan');
    // activate the callback upon this subscription, uni-casting the current data
    // if there's no chatSnapshot, we haven't received it yet from the backend, and it will come soon
    if (this.chatSnapshot != null)
      subscriber.onChatSnapshot(this.chatSnapshot, this.uiLines, this.typingSpeaker); // from addSubscriber, only if Snapshot exists
  }

  removeSubscriber(subscriber: ChatSubscriber) {
    unsubscribe(subscriber, this.subscribers, 'not subscribed to Chan');
    // fix: since we don't get an update ChatSnapshot after unsubscribing, decrease the usage count, the chat
    //      has one less watcher now
    this.chatSnapshot && this.chatSnapshot.stage.watchers--;
  }
}


/**
 * Client-side representation of a streaming queue (next Tweets, for example).
 * Note: this needs to be kept in sync with the counterpart in server/stage_api.
 */
class QueueData {
  // queue data
  readonly queueId: string;
  private contents: any[] = [];
  private updatedTs: number = 0;
  // UI subscribers
  private subscribers: QueueSubscriber[] = [];

  constructor(queueId: string) {
    this.queueId = queueId;
  }

  updateQueue(newContents: any[], updatedTs: number) {
    this.contents = newContents || [];
    this.updatedTs = updatedTs;
    // -> all:onQueueUpdated
    for (const subscriber of this.subscribers)
      subscriber.onQueueUpdated(this.queueId, this.contents, this.updatedTs);   // from onServerQueue
  }

  // subscription methods //
  addSubscriber(subscriber: QueueSubscriber) {
    subscribe(subscriber, this.subscribers, 'already subscribed to Queue');
    // -> subscriber:onQueueUpdated
    if (this.contents.length > 0)
      subscriber.onQueueUpdated(this.queueId, this.contents, this.updatedTs); // from addSubscriber, only if Queue has items
  }

  removeSubscriber(subscriber: QueueSubscriber) {
    unsubscribe(subscriber, this.subscribers, 'not subscribed to Queue');
  }
}


/**
 * Connects to the Stage API Backend to perform operations, and informs react's state of changes
 */
class StageClient {
  serverSocket: SocketIOClient.Socket;
  connected: boolean = false;
  // latest global stats
  stageSnapshot: StageTypes.StageSnapshot = {totalLinesCount: 0, connectionsCount: 0};
  // locally integrated chats - only one at a time is grown, due to the socket only tuning into 1 at once
  chats: { [chatId: string]: ChatData } = {};
  // client-representation of server queues - only one at a time can be subscribed to (bandwidth limiter)
  queues: { [queueId: string]: QueueData } = {};
  // the single chat we're tuned into (used as cache for new subscribers)
  currentChatSnapshot: StageTypes.ChatSnapshot | null = null;

  // subscribers to conf
  subscribersConf: ConfSubscriber[] = [];
  // subscribers to current chat snapshot
  subscribersChatSnapshot: ChatSnapshotSubscriber[] = [];

  constructor() {
    this.serverSocket = io(BACKEND_SERVER, {path: API_SOCKET_STAGE});
    this.serverSocket.on('connect', () => this.onServerConnect());
    this.serverSocket.on('conf', (data) => this.onServerConf(data));
    this.serverSocket.on('chan', (data) => this.onServerChan(data));
    this.serverSocket.on('type', (data) => this.onServerTyping(data));
    this.serverSocket.on('diff', (data) => this.onServerDiff(data));
    this.serverSocket.on('seg', (data) => this.onServerSegment(data));
    this.serverSocket.on('qu', (data) => this.onServerQueueUpdated(data));
    this.serverSocket.on('disconnect', () => this.onServerDisconnected());
  }

  private switchServerChan(chatId: string | null) {
    this.serverSocket.emit('cc', chatId);
  }

  private switchServerQueue(queueId: string | null) {
    this.serverSocket.emit('cq', queueId);
  }

  /// receive commands from the server ///

  onServerConnect() {
    this.connected = true;
  }

  onServerDisconnected() {
    this.connected = false;
    //this.chats.clear(); // NOTE: commented since we can keep the state while the server reconnects
  }

  /**
   * Public channels have changed on the server side. Cloud be more or fewer, the metadata (e.g. watchers)
   * is valid only at this specific point in time.
   */
  onServerConf(msg: StageTypes.MsgConf) {
    // console.log("STAGE CONF")
    // update the stage data
    this.stageSnapshot = msg.stage;
    // update all chat snapshots, creating Data if missing
    msg.chats.forEach(cs =>
      ensureContainer(cs.chatId, this.chats, ChatData)
        .updateSnapshot(cs)
    );
    // -> all:dispatchConf
    for (const subscriber of this.subscribersConf)
      this.dispatchConf(subscriber);
  }

  /**
   * Backend has attached this Client to a channel. You will get this event when switching the backend
   * to this channel, and if some metadata (e.g. topic) but not all (e.g. watchers) changes about the channel.
   */
  onServerChan(msg: StageTypes.MsgChan) {
    // console.log("STAGE CHAN")
    const chatSnapshot = msg.chan;
    const chatId = chatSnapshot.chatId;
    const chatData = getContainer(chatId, this.chats, 'chan on non existing chat');
    if (chatData) chatData.updateSnapshot(chatSnapshot);
    this.setCurrentChatSnapshot(chatSnapshot);
  }

  /**
   * Someone is typing on the current channel.
   */
  onServerTyping(msg: StageTypes.MsgTyping) {
    const chatData = getContainer(msg.chatId, this.chats, 'type on non existing chat');
    if (chatData) chatData.updateTyping(msg.speaker);
  }

  /**
   * One or more lines received for a certain chat.
   */
  onServerDiff(msg: StageTypes.MsgDiff) {
    const chatData = getContainer(msg.chatId, this.chats, 'diff on non existing chat');
    if (chatData) chatData.integrateDiff(msg.chatLines);
  }

  /**
   * New Segment (Story) coming up
   */
  onServerSegment(msg: StageTypes.MsgSegment) {
    const chatData = getContainer(msg.chatId, this.chats, 'segment on non existing chat');
    if (chatData) chatData.updateSegment(msg.segId, msg.segStartTs);
  }

  /**
   * Queue updated
   */
  onServerQueueUpdated(msg: StageTypes.MsgQueueUpdated) {
    const queueData = getContainer(msg.queueId, this.queues, `update on non existing queue ${msg.queueId}`);
    if (queueData) queueData.updateQueue(msg.queueContents, msg.queueUpdatedTs);
  }


  /// manage subscribers to whole config changes (mainly for react state) ///

  subscribeToConf(subscriber: ConfSubscriber) {
    subscribe(subscriber, this.subscribersConf, 'client already subscribed to Conf');
    // if we already have chats, uni-cast them - this also means there's likely no 'conf' call pending
    if (Object.keys(this.chats).length > 0)
      this.dispatchConf(subscriber);
  }

  unsubscribeFromConf(subscriber: ConfSubscriber) {
    unsubscribe(subscriber, this.subscribersConf, 'FIXME: client not subscribed to Conf');
  }

  private dispatchConf(subscriber: ConfSubscriber) {
    const sortedChats = Object.values(this.chats)
      .sort((a: ChatData, b: ChatData) => {
        // first sort by Rank, if present
        const {chatId: aId, chatSnapshot: aSnapshot} = a;
        const {chatId: bId, chatSnapshot: bSnapshot} = b;
        if (aSnapshot && aSnapshot.rank && bSnapshot && bSnapshot.rank)
          if (aSnapshot.rank !== bSnapshot.rank)
            return (aSnapshot.rank < bSnapshot.rank) ? -1 : 1
        // if not present, or equal rank, sort by chatId
        return (aId < bId) ? -1 : 1
      });
    subscriber.onConf(sortedChats, this.stageSnapshot);
  }


  /// chat snapshot subscribers: when the active chat is changing

  subscribeToCurrentChat(subscriber: ChatSnapshotSubscriber) {
    subscribe(subscriber, this.subscribersChatSnapshot, 'client already subscribed to CurrentChat');
    subscriber.onChatSnapshot(this.currentChatSnapshot);
  }

  unsubscribeFromCurrentChat(subscriber: ChatSnapshotSubscriber) {
    unsubscribe(subscriber, this.subscribersChatSnapshot, 'FIXME: client not subscribed to CurrentChat');
  }

  private setCurrentChatSnapshot(chatSnapshot: StageTypes.ChatSnapshot | null) {
    this.currentChatSnapshot = chatSnapshot;
    // -> all:onChatSnapshot
    for (const subscriber of this.subscribersChatSnapshot)
      subscriber.onChatSnapshot(this.currentChatSnapshot);
  }


  /// manage subscription to a Chat (specific ID) ///

  singleSubscribeToChatAndSwitchToIt(chatId: string, chatSubscriber: ChatSubscriber) {
    const chatData = ensureContainer(chatId, this.chats, ChatData); // NOTE: creation can happen here - its params will be updated later
    chatData.addSubscriber(chatSubscriber);
    // request server to change subscription
    this.switchServerChan(chatId);
  }

  singleUnsubscribeFromChat(chatId: string, chatSubscriber: ChatSubscriber) {
    // remove the subscriber
    const chatData = getContainer(chatId, this.chats, 'singleUnsubscribeFromChat on non existing id');
    if (chatData) chatData.removeSubscriber(chatSubscriber);
    // signal loss of chat
    this.setCurrentChatSnapshot(null);
    // request server for 'no-chat' (special id:0) to stop the data through the socket
    this.switchServerChan(null);
  }


  /// manage subscription to a Queue (specific ID) ///

  singleSubscribeToQueueAndSwitchToIt(queueId: string, queueSubscriber: QueueSubscriber) {
    const queueData = ensureContainer(queueId, this.queues, QueueData); // NOTE: creation can happen here - its params will be updated later
    queueData.addSubscriber(queueSubscriber);
    // request server to change subscription
    this.switchServerQueue(queueId);
  }

  singleUnsubscribeFromQueue(queueId: string, queueSubscriber: QueueSubscriber) {
    // remove the subscriber
    const queueData = getContainer(queueId, this.queues, 'singleUnsubscribeFromQueue on non existing id');
    if (queueData) queueData.removeSubscriber(queueSubscriber);
    // switch to 'no-queue' (special id:0) to stop the data through the socket
    this.switchServerQueue(null);
  }
}

const stageClient = new StageClient();
export {stageClient, ChatData};
